import React from 'react';
import { SearchSuggestion, SourceSearchSuggestion } from '@src/types/Filters';
import { style } from 'typestyle';
import { Suggestions } from '../Suggestions';
import { SourceItem } from '../FilterItem/SourceItem';
import { useAtomValue } from 'jotai';
import { FacetState } from "@src/pages/Discover/components/Atoms/Atoms";
import { CustomSourceVariant, SourceUrlKey, SourceUtility } from '@src/fragments/Filters/utils/SourceUtility';
import { UserWithFeaturesView } from '@src/types/common';
import { MentionPageView } from 'common';
import { FiltersSetter, onFilterChange } from '@src/fragments/Filters/utils/FilterStateUtility';
import { SourceNetwork } from '@src/types/SourceNetwork';
import { sourceInput } from '@src/pages/Discover/components/Selectors/Selectors';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import { useFiltersParams } from '@src/hooks/useFiltersParams';

export const getSuggestions = (sourceState: { user: UserWithFeaturesView | null; groupMentionPages: Record<string, MentionPageView> }, input: string): SearchSuggestion<SourceSearchSuggestion>[] => {
  const sources = SourceUtility.getAllDetailedSourcesFromState(sourceState);
  const sourcesWithSearchApplied = sources.filter((s) => SourceUtility.toDisplayLabel(s).indexOf(input) > -1);
  return sourcesWithSearchApplied.map(
      (source) => ({
        key: SourceUtility.sourceToUrlKey(source),
        display: SourceUtility.toDisplayLabel(source),
        network: source.network,
        type: source.type,
        variant: source.variant,
      }),
  );
}

export const getSelectedSuggestion = (sourceFilter: string[]): SearchSuggestion<SourceSearchSuggestion>[] => {
  const suggestions = sourceFilter.map((val) => ({ key: val, display: val }))
  return suggestions.map((suggestion) => {
    const key = XclusionUtility.getRawKey(suggestion.key);
    const source = SourceUtility.urlKeyToSource(key);
    if (!source) {
      return null;
    }
    return {
      ...suggestion,
      key: key,
      display: SourceUtility.toDisplayLabel(source),
      network: source?.network,
      type: source.type,
      variant: source.variant,
    };
  }).filter((s): s is SearchSuggestion<SourceSearchSuggestion> => s !== null)
}

export function selectNetwork(
  network: SourceNetwork,
  sourceState: { user: UserWithFeaturesView | null; groupMentionPages: Record<string, MentionPageView> },
  sourceFilter: string[],
  setFilters: FiltersSetter
  ): void {
  const sources = SourceUtility.getAllDetailedSourcesFromState(sourceState);
  const allSelectedSource = sourceFilter || [];
  const alreadySelected = allSelectedSource.filter((s) => {
    const parsed = SourceUtility.urlKeyToSource(s);
    if (!parsed || parsed.network !== network) {
      return false;
    }
    return network !== 'custom' || !parsed.variant;
  });
  let finalSources: SourceUrlKey[];

  if (alreadySelected.length) {
    finalSources = allSelectedSource.filter((s) => !alreadySelected.includes(s));
  } else {
    const newSelect = sources
      .filter((s) => s.network === network && (network !== 'custom' || !s.variant))
      .map((s) => SourceUtility.sourceToUrlKey(s));
    finalSources = [...allSelectedSource, ...newSelect];
  }
  setFilters((filters) => ({
    ...filters,
    source: finalSources
  }))
}

export function selectCustomVariant(
  variant: CustomSourceVariant,
  sourceState: { user: UserWithFeaturesView | null; groupMentionPages: Record<string, MentionPageView> },
  sourceFilter: string[],
  setFilters: FiltersSetter
): void {
  const sources = SourceUtility.getAllDetailedSourcesFromState(sourceState);
  const allSelectedSource = sourceFilter || [];
  const matchingKeys = sources
    .filter((s) => SourceUtility.isCustomVariantSource(s, variant))
    .map((s) => SourceUtility.sourceToUrlKey(s));
  const alreadySelected = allSelectedSource.filter((s) => matchingKeys.includes(s));

  const finalSources = alreadySelected.length
    ? allSelectedSource.filter((s) => !matchingKeys.includes(s))
    : [...allSelectedSource, ...matchingKeys];

  setFilters((filters) => ({
    ...filters,
    source: finalSources,
  }));
}

export function SourcesSuggestion() {
  const { filters, setFilters } = useFiltersParams();
  const facetState = useAtomValue(FacetState);
  const filterSourceInput = useAtomValue(sourceInput);
  const sourceState = facetState.mediaConstraints.state

  const values = getSuggestions(sourceState, filterSourceInput).filter((s) => s.type !== 'ownContent');
  const selectedValues = getSelectedSuggestion(filters.source)

  return (
    <Suggestions
      onChange={onFilterChange(setFilters, 'source')}
      selectedValues={renderValues(selectedValues)}
      values={renderValues(values)}
    />
  );
}

function renderValues(suggestions: SearchSuggestion<SourceSearchSuggestion>[]) {
  return suggestions.map((suggestion) => ({
    key: suggestion.key,
    children: (
      <SourceItem
        suggestion={suggestion}
        classNames={[sourceItemClass]}
        style={{
          typeNameSpacing: 5,
        }}
      />
    ),
  }));
}

const sourceItemClass = style({
  marginRight: 15,
});
